(() => {
  const RELATED_SELECTOR = 'product-recommendations.related-products';
  const managedRequests = new Map();
  let nativeNoopUrl = '';

  const mark = (name) => {
    try { performance.mark(name); } catch (_) {}
  };

  const scheduleAfterCriticalLoad = (callback) => {
    const scheduleIdle = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 1800 });
      } else {
        window.setTimeout(callback, 900);
      }
    };

    if (document.readyState === 'complete') scheduleIdle();
    else window.addEventListener('load', scheduleIdle, { once: true });
  };

  const fenceNativeRecommendationRequest = (element) => {
    if (element.tagName !== 'PRODUCT-RECOMMENDATIONS') return;
    if (!('Blob' in window) || typeof URL.createObjectURL !== 'function') return;

    try {
      nativeNoopUrl ||= URL.createObjectURL(new Blob([
        '<product-recommendations></product-recommendations>'
      ], { type: 'text/html' }));
      element.setAttribute('data-url', nativeNoopUrl);
    } catch (_) {}
  };

  const limitCards = (root, maximum = 6) => {
    const cards = [...root.querySelectorAll('.grid__item')];
    cards.slice(maximum).forEach((card) => card.remove());
    return Math.min(cards.length, maximum);
  };

  const requestRecommendations = (url) => {
    if (!managedRequests.has(url)) {
      const request = fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then((response) => {
        if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
        return response.text();
      });
      managedRequests.set(url, request);
    }
    return managedRequests.get(url);
  };

  const manageRelatedProducts = (element) => {
    if (!(element instanceof Element) || element.dataset.bpcRelatedManaged === 'true') return;

    const rawUrl = element.getAttribute('data-url');
    if (!rawUrl) return;

    const requestUrl = new URL(rawUrl, window.location.origin);
    requestUrl.searchParams.set('limit', '6');
    fenceNativeRecommendationRequest(element);

    const placeholder = document.createElement('div');
    [...element.attributes].forEach((attribute) => placeholder.setAttribute(attribute.name, attribute.value));
    placeholder.dataset.bpcRelatedManaged = 'true';
    placeholder.setAttribute('aria-busy', 'true');
    placeholder.setAttribute('data-url', requestUrl.toString());
    element.replaceWith(placeholder);

    scheduleAfterCriticalLoad(async () => {
      mark('bpc-related-request-start');
      try {
        const html = await requestRecommendations(requestUrl.toString());
        mark('bpc-related-response');
        if (!placeholder.isConnected) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const source = documentFragment.querySelector(RELATED_SELECTOR)
          || documentFragment.querySelector('product-recommendations');

        if (!source || !source.innerHTML.trim()) {
          placeholder.remove();
          return;
        }

        placeholder.innerHTML = source.innerHTML;
        const cardCount = limitCards(placeholder, 6);
        if (!cardCount) {
          placeholder.remove();
          return;
        }

        placeholder.removeAttribute('data-url');
        placeholder.setAttribute('aria-busy', 'false');
        placeholder.classList.add('bpc-related-products-ready');
        mark('bpc-related-visible-ready');
        try {
          performance.measure(
            'bpc-related-fetch-to-ready',
            'bpc-related-request-start',
            'bpc-related-visible-ready'
          );
        } catch (_) {}
        placeholder.dispatchEvent(new CustomEvent('bpc:related-products:ready', {
          bubbles: true,
          detail: { cardCount }
        }));
      } catch (_) {
        mark('bpc-related-request-failed');
        placeholder.remove();
      }
    });
  };

  const init = (root = document) => {
    if (root instanceof Element && root.matches(RELATED_SELECTOR)) manageRelatedProducts(root);
    root.querySelectorAll?.(RELATED_SELECTOR).forEach(manageRelatedProducts);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('pagehide', () => {
    if (!nativeNoopUrl) return;
    try { URL.revokeObjectURL(nativeNoopUrl); } catch (_) {}
    nativeNoopUrl = '';
  }, { once: true });
})();
